import { useState } from 'react'
import { Download, Info } from 'lucide-react'
import { DateValue, EmptyState, ErrorState, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { downloadMyPayslip, useMyPayslip, useMyPayslips } from '../api'
import { monthName } from '../types'
import type { PayrollLineView } from '../types'

/**
 * Your own pay.
 *
 * <p>The only payroll screen an employee can open, and the only one that asks for nothing but
 * "mine" — the endpoints behind it take no employee id at all. A period appears here once the
 * employer has approved it: showing a calculated figure that can still move would have people
 * planning around a number nobody has agreed to.
 */
export function MyPayslipsPage() {
  useBreadcrumb()
  const payslips = useMyPayslips()
  const [openRunId, setOpenRunId] = useState<string | null>(null)
  const detail = useMyPayslip(openRunId ?? undefined)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader title="My payslips" subtitle="What you were paid, and what was taken off." />

      {error && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      <Card>
        {payslips.isPending && <Skeleton className="m-4 h-24" />}
        {payslips.isError && (
          <ErrorState error={payslips.error} onRetry={() => void payslips.refetch()} />
        )}
        {payslips.isSuccess && payslips.data.length === 0 && (
          <EmptyState
            title="No payslip yet"
            description="A period appears here once your employer has approved that payroll run."
          />
        )}
        {payslips.isSuccess && payslips.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                <tr>
                  <th className="p-3 text-start font-medium">Period</th>
                  <th className="p-3 text-end font-medium">Gross</th>
                  <th className="p-3 text-end font-medium">Deductions</th>
                  <th className="p-3 text-end font-medium">Net</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {payslips.data.map((slip) => (
                  <tr key={slip.runId} className="border-b border-[var(--color-border)]">
                    <td className="p-3">
                      <button
                        type="button"
                        className="font-medium text-[var(--color-primary)] underline-offset-2 hover:underline"
                        onClick={() => setOpenRunId(slip.runId === openRunId ? null : slip.runId)}
                      >
                        {monthName(slip.periodYear, slip.periodMonth)}
                      </button>
                      <span className="block text-xs text-[var(--color-muted)]">
                        <DateValue value={slip.periodStart} /> – <DateValue value={slip.periodEnd} />
                      </span>
                    </td>
                    <td className="p-3 text-end">{slip.currency} {slip.grossPay}</td>
                    <td className="p-3 text-end">{slip.currency} {slip.totalDeductions}</td>
                    <td className="p-3 text-end font-medium">{slip.currency} {slip.netPay}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={slip.status}
                        tone={slip.status === 'PAID' || slip.status === 'CLOSED' ? 'success' : 'info'}
                        label={slip.status === 'APPROVED' ? 'Approved' : 'Paid'}
                      />
                      {slip.paymentNote && (
                        <span className="block text-xs text-[var(--color-muted)]">
                          {slip.paymentNote}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-end">
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => {
                          void (async () => {
                            setError(null)
                            try {
                              await downloadMyPayslip(
                                slip.runId,
                                `payslip-${slip.periodYear}-${String(slip.periodMonth).padStart(2, '0')}.pdf`)
                            } catch (caught) {
                              setError(toApiProblem(caught)?.detail ?? 'That payslip could not be downloaded')
                            }
                          })()
                        }}
                      >
                        <Download className="size-4" aria-hidden="true" />
                        PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openRunId && (
        <Card className="p-3">
          {detail.isPending && <Skeleton className="h-40" />}
          {detail.isError && <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />}
          {detail.isSuccess && (
            <div className="space-y-4">
              <p className="text-sm font-medium text-[var(--color-heading)]">
                {detail.data.employeeName} · <DateValue value={detail.data.periodStart} /> –{' '}
                <DateValue value={detail.data.periodEnd} />
              </p>
              <Lines title="Earnings" lines={detail.data.earnings} currency={detail.data.currency} />
              <Lines title="Deductions" lines={detail.data.deductions} currency={detail.data.currency} />
              {detail.data.employerContributions.length > 0 && (
                <>
                  <Lines
                    title="Paid by your employer"
                    lines={detail.data.employerContributions}
                    currency={detail.data.currency}
                  />
                  <p className="flex items-start gap-2 text-xs text-[var(--color-muted)]">
                    <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                    These are paid on top of your salary, not taken out of it.
                  </p>
                </>
              )}
              <p className="text-sm font-medium text-[var(--color-heading)]">
                Net pay: {detail.data.currency} {detail.data.netPay}
              </p>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

function Lines({
  title, lines, currency,
}: { title: string; lines: PayrollLineView[]; currency: string | null }) {
  if (lines.length === 0) {
    return (
      <div>
        <p className="text-sm font-medium text-[var(--color-heading)]">{title}</p>
        <p className="text-sm text-[var(--color-muted)]">None this period.</p>
      </div>
    )
  }
  return (
    <div>
      <p className="text-sm font-medium text-[var(--color-heading)]">{title}</p>
      <table className="w-full text-sm">
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="border-b border-[var(--color-border)]">
              <td className="py-1.5">{line.elementName ?? line.elementCode}</td>
              <td className="py-1.5 text-end">{currency} {line.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
